import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type ProductCategoryDTO } from "../../api";
import { PRODUCT_FAMILY_VARIANT } from "../../components/badges";
import { EnumBadge } from "../../components/cells";
import { checkboxField, crudEditor, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Product categories.
 *
 * Family and code are assigned when the category is seeded and are never
 * editable — every SKU in the catalogue is built from them, so the Vaadin grid
 * offered edit-only rows with those two columns read-only, and no add/delete.
 */
export function ProductCategoriesPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.PRODUCT_CATEGORY);
  const title = t("navigation.item.product-categories", "Categories");

  const { data, isLoading, error } = useQuery({
    queryKey: ["product", "category"],
    queryFn: () => api.productCategories.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["product", "category"] });

  const cols = useMemo(() => {
    const c = defineColumns<ProductCategoryDTO>();
    const flag = (value: boolean) =>
      value ? <Badge variant="success">✓</Badge> : <span className="text-ink-faint">—</span>;
    return [
      c.custom(
        "family",
        (row) => row.family,
        (row) => <EnumBadge value={row.family} variants={PRODUCT_FAMILY_VARIANT} />,
        { headerKey: "column.family", filter: "select", priority: 2 },
      ),
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
      c.text("label", (row) => row.label, { headerKey: "column.label", priority: 2 }),
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.text("material", (row) => row.material, { headerKey: "column.material", priority: 4 }),
      c.text("color", (row) => row.color, { headerKey: "column.color", priority: 4 }),
      c.custom("framed", (row) => String(row.framed), (row) => flag(row.framed), {
        headerKey: "column.framed",
        filter: "select",
        priority: 5,
      }),
      c.custom("assembled", (row) => String(row.assembled), (row) => flag(row.assembled), {
        headerKey: "column.assembled",
        filter: "select",
        priority: 5,
      }),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<ProductCategoryDTO>({
        client: api.productCategories,
        refresh,
        t,
        canDelete: false,
        fields: {
          label: textField(
            (row) => row.label,
            (row, value) => ({ ...row, label: value }),
            true,
          ),
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          material: textField(
            (row) => row.material,
            (row, value) => ({ ...row, material: value }),
          ),
          color: textField(
            (row) => row.color,
            (row, value) => ({ ...row, color: value }),
          ),
          framed: checkboxField(
            (row) => row.framed,
            (row, value) => ({ ...row, framed: value }),
          ),
          assembled: checkboxField(
            (row) => row.assembled,
            (row, value) => ({ ...row, assembled: value }),
          ),
        },
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          if (!row.label?.trim()) errors.label = required;
          if (!row.name?.trim()) errors.name = required;
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<ProductCategoryDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        editor={editor}
      />
    </>
  );
}
